import type { CanvasAgentId, DutyId } from "./agentRoster";
import { deepFreeze } from "./deepFreeze";
import { CANVAS_AGENTS, DUTIES, agentOfDuty } from "./agentRoster";

/**
 * Role charters, layer L0 (Agent Brains section 10): what each canvas agent is
 * for, what it must never do, who it hands off to and when it asks instead of
 * acting. T3: a human-edited constant, never configuration and never a thing
 * an agent proposes. `roleCharters.test.ts` restates every row, so a change is
 * never a one-line diff.
 *
 * A role charter adds boundaries and a hand-off map. It does not loosen any
 * gate: the gate chain (CLAUDE.md section 2), the send path (section 8) and
 * T3 (section 3) hold whatever a charter says.
 */

export type ContractKind =
  | "lead_card"
  | "lead_score"
  | "reply_draft"
  | "booking_intent"
  | "call_prep_brief"
  | "reactivation_request"
  | "call_list"
  | "review_ask_draft"
  | "review_response_draft"
  | "review_synthesis"
  | "content_suggestion"
  | "insight"
  | "digest"
  | "proposal"
  | "campaign_plan"
  | "outcome_label";

export const CONTRACT_KINDS: readonly ContractKind[] = [
  "lead_card",
  "lead_score",
  "reply_draft",
  "booking_intent",
  "call_prep_brief",
  "reactivation_request",
  "call_list",
  "review_ask_draft",
  "review_response_draft",
  "review_synthesis",
  "content_suggestion",
  "insight",
  "digest",
  "proposal",
  "campaign_plan",
  "outcome_label",
];

/** The closed list of "never" ids, across all seven agents. A shared id (free_send_out_of_window) appears once. */
export const NEVER_IDS = [
  // research
  "contact_customer",
  "inference_as_fact",
  "reveal_card",
  "exceed_lookup_cap",
  // reputation
  "ask_below_4_or_open_complaint",
  "incentivize_review",
  "decide_write_back",
  "publish_content",
  // shepherd
  "segment_wave",
  "own_voice_copy",
  "nudge_pending_or_escalated",
  "rank_by_recency",
  "free_send_out_of_window",
  // analyst
  "act",
  "llm_arithmetic",
  "correlation_as_cause",
  "hide_n",
  "soften_number",
  // nurture
  "unsourced_figure",
  "offer_or_discount",
  "book_by_prose",
  "claim_human",
  "memory_outside_gate",
  // autolearn
  "write_t3",
  "ship_without_citations",
  "ship_unanswered_grill",
  "learn_from_heldout",
  "refreeze_baseline",
  "run_without_charter",
  // campaign
  "per_person_clock",
  "send_without_no_offer_confirm",
  "break_window_or_optin",
  "exceed_global_cap",
  "offer_in_template",
] as const;
export type NeverId = (typeof NEVER_IDS)[number];

/** The closed list of "ask instead of act" ids. */
export const ASK_IDS = [
  // research
  "sources_contradict",
  "identity_match_unsure",
  // reputation
  "complaint_refund_or_legal",
  // shepherd
  "vip_carve_out",
  "sensitive_date_doubt",
  // analyst
  "n_below_floor",
  // nurture
  "kb_no_answer",
  "price_beyond_kb",
  "class_d",
  "vip",
  // autolearn
  "any_t2",
  "charter_conflict",
  "needs_unmet",
  // campaign
  "every_new_template",
  "suppression_doubt",
] as const;
export type AskId = (typeof ASK_IDS)[number];

export type Link = { readonly agent: CanvasAgentId | DutyId | "owner"; readonly contract: ContractKind };

export interface RoleCharter {
  readonly job: string;
  readonly never: readonly NeverId[];
  readonly receivesFrom: readonly Link[];
  readonly handsOffTo: readonly Link[];
  readonly produces: readonly ContractKind[];
  readonly askWhen: readonly AskId[];
  readonly duties: readonly DutyId[];
}

const L = (agent: Link["agent"], contract: ContractKind): Link => ({ agent, contract });

export const ROLE_CHARTERS: Readonly<Record<CanvasAgentId, RoleCharter>> = deepFreeze<Record<CanvasAgentId, RoleCharter>>({
  research: {
    job: "Build and refresh the lead card: evidence-kinded facts, scored inferences, TTL.",
    never: ["contact_customer", "inference_as_fact", "reveal_card", "exceed_lookup_cap"],
    receivesFrom: [L("nurture", "lead_score"), L("autolearn", "proposal")],
    handsOffTo: [
      L("reputation", "lead_card"),
      L("shepherd", "lead_card"),
      L("analyst", "lead_card"),
      L("nurture", "lead_card"),
      L("autolearn", "lead_card"),
      L("campaign", "lead_card"),
      L("owner", "lead_card"),
    ],
    produces: ["lead_card", "lead_score"],
    askWhen: ["sources_contradict", "identity_match_unsure"],
    duties: [],
  },
  reputation: {
    job: "Ask for reviews at the peak moment, recover unhappy customers privately, draft responses, synthesize themes.",
    never: ["ask_below_4_or_open_complaint", "incentivize_review", "decide_write_back", "publish_content"],
    receivesFrom: [L("research", "lead_card"), L("autolearn", "proposal")],
    handsOffTo: [
      L("nurture", "review_response_draft"),
      L("campaign", "content_suggestion"),
      L("owner", "review_response_draft"),
    ],
    produces: ["review_ask_draft", "review_response_draft", "review_synthesis", "content_suggestion"],
    askWhen: ["complaint_refund_or_legal"],
    duties: [],
  },
  shepherd: {
    job: "Nothing rots: per-lead clocks, EV call list, per-person occasion reactivation.",
    never: [
      "segment_wave",
      "own_voice_copy",
      "nudge_pending_or_escalated",
      "rank_by_recency",
      "free_send_out_of_window",
    ],
    receivesFrom: [
      L("research", "lead_card"),
      L("analyst", "insight"),
      L("campaign", "reactivation_request"),
      L("autolearn", "proposal"),
    ],
    handsOffTo: [
      L("nurture", "reactivation_request"),
      L("owner", "call_list"),
      L("campaign", "campaign_plan"),
    ],
    produces: ["reactivation_request", "call_list", "campaign_plan"],
    askWhen: ["vip_carve_out", "sensitive_date_doubt"],
    duties: [],
  },
  analyst: {
    job: "Measure and explain with deterministic, dollars-first aggregates that state n.",
    never: ["act", "llm_arithmetic", "correlation_as_cause", "hide_n", "soften_number"],
    receivesFrom: [L("research", "lead_card"), L("autolearn", "proposal")],
    handsOffTo: [L("campaign", "insight"), L("owner", "insight"), L("shepherd", "insight")],
    produces: ["insight", "digest"],
    askWhen: ["n_below_floor"],
    duties: [],
  },
  nurture: {
    job: "Hold the live conversation from the KB, qualify, move toward consult or deposit; schedule owns the calendar.",
    never: [
      "unsourced_figure",
      "offer_or_discount",
      "book_by_prose",
      "claim_human",
      "free_send_out_of_window",
      "memory_outside_gate",
    ],
    receivesFrom: [
      L("research", "lead_card"),
      L("reputation", "review_response_draft"),
      L("shepherd", "reactivation_request"),
      L("campaign", "campaign_plan"),
      L("autolearn", "proposal"),
    ],
    handsOffTo: [
      L("nurture.schedule", "booking_intent"),
      L("owner", "reply_draft"),
      L("research", "lead_score"),
    ],
    produces: ["reply_draft", "lead_score", "booking_intent", "call_prep_brief"],
    askWhen: ["kb_no_answer", "price_beyond_kb", "class_d", "vip"],
    duties: ["nurture.reply", "nurture.qualify", "nurture.schedule"],
  },
  autolearn: {
    job: "Turn labelled outcomes into cited, self-grilled proposals; ship T1 only past floors, eval and shadow; retract misses. Onboard drafts v1 artifacts, all T2.",
    never: [
      "write_t3",
      "ship_without_citations",
      "ship_unanswered_grill",
      "learn_from_heldout",
      "refreeze_baseline",
      "run_without_charter",
    ],
    receivesFrom: [L("research", "lead_card")],
    handsOffTo: [
      L("owner", "proposal"),
      L("research", "proposal"),
      L("reputation", "proposal"),
      L("shepherd", "proposal"),
      L("analyst", "proposal"),
      L("nurture", "proposal"),
      L("campaign", "proposal"),
    ],
    produces: ["proposal", "outcome_label"],
    askWhen: ["any_t2", "charter_conflict", "needs_unmet"],
    duties: ["autolearn.onboard"],
  },
  campaign: {
    job: "Run one-to-many waves (seasonal, trigger, opt-in, review-ask waves) for campaign ROI.",
    never: [
      "per_person_clock",
      "send_without_no_offer_confirm",
      "break_window_or_optin",
      "exceed_global_cap",
      "offer_in_template",
    ],
    receivesFrom: [
      L("research", "lead_card"),
      L("reputation", "content_suggestion"),
      L("shepherd", "campaign_plan"),
      L("analyst", "insight"),
      L("autolearn", "proposal"),
    ],
    handsOffTo: [
      L("owner", "campaign_plan"),
      L("nurture", "campaign_plan"),
      L("shepherd", "reactivation_request"),
    ],
    produces: ["campaign_plan", "reactivation_request"],
    askWhen: ["every_new_template", "suppression_doubt"],
    duties: [],
  },
});

/** The agent a link points at, or null for the owner. A duty resolves to its agent. */
export function linkTargetAgent(link: Link): CanvasAgentId | null {
  if (link.agent === "owner") return null;
  return (CANVAS_AGENTS as readonly string[]).includes(link.agent)
    ? (link.agent as CanvasAgentId)
    : agentOfDuty(link.agent as DutyId);
}

/**
 * The trusted lines of an agent's L0 block. TRUSTED text, authored by a human
 * in this file: it is the one place a prompt may state boundaries, and it
 * carries ids and the charter's own sentences only, never customer text and
 * never memory. `duty`, when given, must be one of the agent's duties.
 *
 * Fails loudly (section 0) on an agent with no charter or a duty that is not
 * its own: a prompt built from a wrong charter is worse than none.
 */
export function renderRoleCharter(agent: CanvasAgentId, duty?: DutyId): string[] {
  const charter = (ROLE_CHARTERS as Readonly<Record<string, RoleCharter | undefined>>)[agent];
  if (charter === undefined) throw new Error(`renderRoleCharter: no role charter for agent ${JSON.stringify(agent)}`);
  if (duty !== undefined) {
    if (!(DUTIES as readonly string[]).includes(duty) || !charter.duties.includes(duty)) {
      throw new Error(`renderRoleCharter: ${JSON.stringify(duty)} is not a duty of ${agent}`);
    }
  }
  const link = (l: Link): string => `${l.agent} (${l.contract})`;
  const lines = [
    "ROLE CHARTER (trusted, written by a person)",
    `Agent: ${agent}`,
    ...(duty !== undefined ? [`Duty: ${duty}`] : []),
    `Job: ${charter.job}`,
    `Never: ${charter.never.join(", ")}`,
    `Receives from: ${charter.receivesFrom.length === 0 ? "none" : charter.receivesFrom.map(link).join(", ")}`,
    `Hands off to: ${charter.handsOffTo.length === 0 ? "none" : charter.handsOffTo.map(link).join(", ")}`,
    `Produces: ${charter.produces.join(", ")}`,
    `Ask instead of acting when: ${charter.askWhen.join(", ")}`,
  ];
  return lines;
}
